import JSZip from "jszip";
import type { Mesh } from "./mesh3d";

// 3MF is a zip with 3D/3dmodel.model (XML). Slicers often put the real geometry in other .model files and reference it
// from the main one with <component p:path="...">, so those are followed. Build items and components carry 4x3 transforms.
const UNIT_TO_MM: Record<string, number> = { micron: 0.001, millimeter: 1, centimeter: 10, inch: 25.4, foot: 304.8, meter: 1000 };

type M = number[]; // row-vector 4x3: [m00 m01 m02 m10 m11 m12 m20 m21 m22 m30 m31 m32]
const IDENT: M = [1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0];
const parseM = (s: string | null): M => {
  if (!s) return IDENT;
  const a = s.trim().split(/\s+/).map(Number);
  return a.length === 12 && a.every(Number.isFinite) ? a : IDENT;
};
/** a then b (row-vector convention: p' = p·A, then ·B). */
const mul = (a: M, b: M): M => {
  const r = (i: number, j: number) => (i < 3 ? a[i * 3] * b[j] + a[i * 3 + 1] * b[3 + j] + a[i * 3 + 2] * b[6 + j] : a[9] * b[j] + a[10] * b[3 + j] + a[11] * b[6 + j] + b[9 + j]);
  return [r(0, 0), r(0, 1), r(0, 2), r(1, 0), r(1, 1), r(1, 2), r(2, 0), r(2, 1), r(2, 2), r(3, 0), r(3, 1), r(3, 2)];
};

interface Obj { verts?: number[]; tris?: number[]; comps?: { id: string; path: string | null; m: M }[]; type: string }

const attr = (el: Element, n: string) => el.getAttribute(n) ?? el.getAttributeNS("*", n) ?? Array.from(el.attributes).find((a) => a.localName === n)?.value ?? null;
const kids = (el: Element, n: string) => Array.from(el.getElementsByTagNameNS("*", n));

function readObjects(xml: string): { objects: Map<string, Obj>; build: { id: string; path: string | null; m: M }[]; unit: number } {
  const doc = new DOMParser().parseFromString(xml, "application/xml");
  if (doc.getElementsByTagName("parsererror").length) throw new Error("This 3MF file's model XML is damaged");
  const model = doc.documentElement;
  const unit = UNIT_TO_MM[(attr(model, "unit") || "millimeter").toLowerCase()] ?? 1;
  const objects = new Map<string, Obj>();
  for (const o of kids(model, "object")) {
    const id = attr(o, "id");
    if (id === null) continue;
    const obj: Obj = { type: (attr(o, "type") || "model").toLowerCase() };
    const mesh = kids(o, "mesh")[0];
    if (mesh) {
      obj.verts = []; obj.tris = [];
      for (const v of kids(mesh, "vertex")) obj.verts.push(Number(attr(v, "x")), Number(attr(v, "y")), Number(attr(v, "z")));
      for (const t of kids(mesh, "triangle")) obj.tris.push(Number(attr(t, "v1")), Number(attr(t, "v2")), Number(attr(t, "v3")));
    }
    const comps = kids(o, "component");
    if (comps.length) obj.comps = comps.map((c) => ({ id: attr(c, "objectid") || "", path: attr(c, "path"), m: parseM(attr(c, "transform")) }));
    objects.set(id, obj);
  }
  const build = kids(model, "item").map((i) => ({ id: attr(i, "objectid") || "", path: attr(i, "path"), m: parseM(attr(i, "transform")) }));
  return { objects, build, unit };
}

/** Reads every build item of a 3MF file into one triangle mesh, in millimetres. Support objects and modifiers are skipped. */
export async function parse3MF(buf: ArrayBuffer): Promise<Mesh> {
  const zip = await JSZip.loadAsync(buf);
  const text = async (name: string) => {
    const f = zip.file(name) ?? zip.file(name.replace(/^\//, ""));
    return f ? f.async("text") : null;
  };
  let mainName = "3D/3dmodel.model";
  const rels = await text("_rels/.rels");
  if (rels) {
    const m = /Type="[^"]*3dmodel"[^>]*Target="([^"]+)"|Target="([^"]+)"[^>]*Type="[^"]*3dmodel"/.exec(rels);
    const t = m?.[1] ?? m?.[2];
    if (t) mainName = t.replace(/^\//, "");
  }
  const mainXml = await text(mainName);
  if (!mainXml) throw new Error("This doesn't look like a 3MF file (no 3D model inside)");
  const main = readObjects(mainXml);
  const files = new Map<string, ReturnType<typeof readObjects>>([[mainName, main]]);

  const vertices: number[] = [];
  const triangles: number[][] = [];
  const unit = main.unit;
  const MAX_DEPTH = 8;

  const emit = async (file: string, id: string, m: M, depth: number): Promise<void> => {
    if (depth > MAX_DEPTH) throw new Error("This 3MF nests its parts too deeply to read");
    let src = files.get(file);
    if (!src) {
      const x = await text(file);
      if (!x) return;
      src = readObjects(x);
      files.set(file, src);
    }
    const o = src.objects.get(id);
    if (!o || o.type === "support" || o.type === "solidsupport" || o.type === "surface") return;
    if (o.verts && o.tris && o.type !== "other") {
      const base = vertices.length / 3;
      for (let i = 0; i < o.verts.length; i += 3) {
        const x = o.verts[i], y = o.verts[i + 1], z = o.verts[i + 2];
        vertices.push(
          (x * m[0] + y * m[3] + z * m[6] + m[9]) * unit,
          (x * m[1] + y * m[4] + z * m[7] + m[10]) * unit,
          (x * m[2] + y * m[5] + z * m[8] + m[11]) * unit,
        );
      }
      const nV = o.verts.length / 3;
      // A mirroring transform flips the winding; flip the triangles back so normals still point outward.
      const det = m[0] * (m[4] * m[8] - m[5] * m[7]) - m[1] * (m[3] * m[8] - m[5] * m[6]) + m[2] * (m[3] * m[7] - m[4] * m[6]);
      for (let i = 0; i < o.tris.length; i += 3) {
        const a = o.tris[i], b = o.tris[i + 1], c = o.tris[i + 2];
        if (!(a < nV && b < nV && c < nV && a >= 0 && b >= 0 && c >= 0)) continue;
        triangles.push(det < 0 ? [base + a, base + c, base + b] : [base + a, base + b, base + c]);
      }
    }
    for (const c of o.comps ?? []) await emit(c.path ? c.path.replace(/^\//, "") : file, c.id, mul(c.m, m), depth + 1);
  };

  for (const item of main.build) await emit(item.path ? item.path.replace(/^\//, "") : mainName, item.id, item.m, 0);
  if (!triangles.length) throw new Error("No printable geometry found in this 3MF file");
  return { vertices, triangles };
}

/** Writes a single-object 3MF in millimetres. */
export async function write3MF(mesh: Mesh, name = "model"): Promise<Blob> {
  const esc = (t: string) => t.replace(/[<>&"]/g, "");
  const v: string[] = [];
  for (let i = 0; i < mesh.vertices.length; i += 3) v.push(`<vertex x="${mesh.vertices[i]}" y="${mesh.vertices[i + 1]}" z="${mesh.vertices[i + 2]}"/>`);
  const t = mesh.triangles.map((f) => `<triangle v1="${f[0]}" v2="${f[1]}" v3="${f[2]}"/>`);
  const model = `<?xml version="1.0" encoding="UTF-8"?>\n<model unit="millimeter" xml:lang="en-US" xmlns="http://schemas.microsoft.com/3dmanufacturing/core/2015/02"><metadata name="Title">${esc(name)}</metadata><metadata name="Application">FreeDailyPro 3D Mesh Converter</metadata><resources><object id="1" type="model"><mesh><vertices>${v.join("")}</vertices><triangles>${t.join("")}</triangles></mesh></object></resources><build><item objectid="1"/></build></model>`;
  const z = new JSZip();
  z.file("[Content_Types].xml", `<?xml version="1.0" encoding="UTF-8"?>\n<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="model" ContentType="application/vnd.ms-package.3dmanufacturing-3dmodel+xml"/></Types>`);
  z.file("_rels/.rels", `<?xml version="1.0" encoding="UTF-8"?>\n<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Target="/3D/3dmodel.model" Id="rel0" Type="http://schemas.microsoft.com/3dmanufacturing/2013/01/3dmodel"/></Relationships>`);
  z.file("3D/3dmodel.model", model);
  return z.generateAsync({ type: "blob", mimeType: "application/vnd.ms-package.3dmanufacturing-3dmodel+xml", compression: "DEFLATE" });
}
